import type { PointerEvent as ReactPointerEvent } from 'react';

/**
 * Follows one pointer drag to wherever it ends.
 *
 * The listeners go on the window, not on the handle that started the drag.
 * Pointer capture is still requested — it keeps hover states off everything
 * the pointer crosses on the way — but it is a courtesy rather than the
 * mechanism. `setPointerCapture` throws on a stale or synthetic pointer id,
 * and a handle listening only to itself then never heard the pointerup that
 * ended the drag: the drag stayed armed, and the next time the pointer merely
 * crossed that handle the edge moved without anyone touching it.
 *
 * The pointer id is checked on every event so a second finger cannot drive
 * someone else's drag.
 */
export function trackDrag(
  e: ReactPointerEvent<HTMLElement>,
  onMove: (ev: PointerEvent) => void,
  onEnd?: () => void,
): void {
  const el = e.currentTarget;
  const { pointerId } = e;

  try {
    el.setPointerCapture(pointerId);
  } catch {
    // Nothing to capture. The window listeners below are what matter.
  }

  const move = (ev: PointerEvent) => {
    if (ev.pointerId === pointerId) onMove(ev);
  };

  const end = (ev: PointerEvent) => {
    if (ev.pointerId !== pointerId) return;
    try {
      el.releasePointerCapture(pointerId);
    } catch {
      // Never captured, or already released.
    }
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', end);
    window.removeEventListener('pointercancel', end);
    onEnd?.();
  };

  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', end);
  window.addEventListener('pointercancel', end);
}
